import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import KPITile from "@/components/shared/KPITile";
import SeverityBadge from "@/components/shared/SeverityBadge";
import { Badge } from "@/components/ui/badge";
import {
  Flame, AlertTriangle, Truck, Activity,
  Clock, ChevronRight, Loader2, RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { extractApiError } from "@/lib/api";
import { downloadReport } from "@/lib/downloadReport";
import { fetchIncidentStatusCounts, getByStatusCount } from "@/lib/incidentStatusCounts";
import { useIncidentSocket } from "@/hooks/useIncidentSocket";
import LocationLabel from "@/components/shared/LocationLabel";

function normalizeCollection(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function normalizeSeverity(value) {
  const severity = String(value || "low").toLowerCase();
  return ["critical", "high", "medium", "low"].includes(severity) ? severity : "low";
}

function formatIncidentType(value) {
  return String(value || "Incident")
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatTime(value) {
  if (!value) return "—";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return String(value);
  return dt.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatMinutes(valueInSeconds) {
  const numeric = Number(valueInSeconds);
  if (!Number.isFinite(numeric)) return "—";
  return `${Math.round(numeric / 60)} min`;
}

function mapIncident(item) {
  return {
    id: item?.id || item?.incident_id || "",
    status: String(item?.status || "OPEN").toUpperCase(),
    type: formatIncidentType(item?.incident_type || item?.type),
    severity: normalizeSeverity(item?.severity),
    locationRaw: item?.location ?? item?.address ?? item?.place ?? null,
    location:
      item?.location?.address ||
      item?.location ||
      item?.address ||
      item?.place ||
      [item?.city, item?.state].filter(Boolean).join(", ") ||
      "Location unavailable",
    timestamp: item?.event_time || item?.created_at || item?.updated_at || item?.timestamp || "",
  };
}

export default function FireDashboard() {
  const navigate = useNavigate();
  const [openIncidents, setOpenIncidents] = useState([]);
  const [openIncidentsCount, setOpenIncidentsCount] = useState(0);
  const [respondingCount, setRespondingCount] = useState(0);
  const [avgArrivalSeconds, setAvgArrivalSeconds] = useState(null);
  const [liveCount, setLiveCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const [openResp, counts] = await Promise.all([
        api.get("/incidents", { params: { status: "OPEN", page_size: 5 } }),
        fetchIncidentStatusCounts(),
      ]);

      const openItems = normalizeCollection(openResp.data).map(mapIncident).filter((x) => x.id);
      setOpenIncidents(openItems);
      // Missing status key in by_status means 0
      setOpenIncidentsCount(getByStatusCount(counts.by_status, "OPEN"));
      setRespondingCount(getByStatusCount(counts.by_status, "RESPONDING"));
    } catch (err) {
      const { message } = extractApiError(err);
      setError(message || "Failed to load fire dashboard.");
      setOpenIncidents([]);
      setOpenIncidentsCount(0);
      setRespondingCount(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  useEffect(() => {
    let isMounted = true;

    const fetchArrivalAnalytics = async () => {
      try {
        const response = await api.get("/analytics/incidents/summary", { params: { period: "7d" } });
        if (!isMounted) return;

        const avgArrival = response?.data?.data?.avg_arrival_seconds;
        setAvgArrivalSeconds(Number.isFinite(Number(avgArrival?.value)) ? Number(avgArrival.value) : null);
      } catch (err) {
        if (!isMounted) return;
        const { message } = extractApiError(err);
        console.warn("[FireDashboard] Failed to load arrival analytics:", message || err?.message || err);
        setAvgArrivalSeconds(null);
      }
    };

    fetchArrivalAnalytics();

    return () => {
      isMounted = false;
    };
  }, []);

  useIncidentSocket((incidentData) => {
    const incident = mapIncident(incidentData);
    if (!incident.id) return;

    setOpenIncidents((prev) => {
      const exists = prev.some((item) => item.id === incident.id);
      if (incident.status !== "OPEN") {
        if (!exists) return prev;
        setOpenIncidentsCount((count) => Math.max(0, count - 1));
        return prev.filter((item) => item.id !== incident.id);
      }

      if (exists) return prev.map((item) => (item.id === incident.id ? incident : item));

      setOpenIncidentsCount((count) => count + 1);
      setLiveCount((count) => count + 1);
      return [incident, ...prev].slice(0, 5);
    });
  });

  const reportRows = useMemo(
    () => [
      ["Open Alerts", openIncidentsCount],
      ["Active Deployments", respondingCount],
      ["Live Alert Pushes", liveCount],
    ],
    [openIncidentsCount, respondingCount, liveCount],
  );

  return (
    <div className="space-y-6" data-testid="fire-dashboard">
      <PageHeader
        title="Dashboard"
        subtitle="Fire Brigade · Operational overview with live incident preview."
        onDownload={() => {
          downloadReport({
            filename: "fire_dashboard",
            title: "Fire Dashboard Summary",
            headers: ["Metric", "Value"],
            rows: reportRows,
          });
          toast.success("Report downloaded");
        }}
        actions={
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-200">Live +{liveCount}</Badge>
            <Button size="sm" variant="outline" onClick={fetchDashboardData} disabled={loading}>
              {loading ? <Loader2 size={14} className="mr-1 animate-spin" /> : <RefreshCw size={14} className="mr-1" />} Refresh
            </Button>
            <Button size="sm" variant="outline" onClick={() => navigate("/fire/active")}>
              <Flame size={14} className="mr-1" /> Active Incidents
            </Button>
          </div>
        }
      />

      {error && <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
        <KPITile icon={AlertTriangle} label="Open alerts" value={String(openIncidentsCount)} accent="rose" />
        <KPITile icon={Truck} label="Active deployments" value={String(respondingCount)} accent="amber" />
        <KPITile icon={Activity} label="Live pushes" value={String(liveCount)} accent="blue" />
        <KPITile icon={Clock} label="Avg arrival" value={formatMinutes(avgArrivalSeconds)} accent="slate" />
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="space-y-5 xl:col-span-2">
          <SectionCard title="Recent Alerts" subtitle="Preview of open incidents routed to the fire department.">
            {loading && openIncidents.length === 0 ? (
              <div className="flex items-center justify-center py-12 text-slate-500">
                <Loader2 size={18} className="mr-2 animate-spin" /> Loading alerts...
              </div>
            ) : openIncidents.length === 0 ? (
              <p className="py-4 text-sm text-slate-500">No open incidents.</p>
            ) : (
              <div className="space-y-0">
                {openIncidents.map((incident) => (
                  <button
                    key={incident.id}
                    type="button"
                    onClick={() => navigate("/fire/alerts")}
                    className="flex w-full items-center gap-3 border-b border-slate-100 py-3 text-left last:border-b-0"
                  >
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-rose-50 text-rose-600">
                      <AlertTriangle size={15} strokeWidth={1.8} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-slate-900">{incident.type}</p>
                      <LocationLabel locationValue={incident.locationRaw ?? incident.location} className="truncate text-[11px] text-slate-500" />
                      <p className="text-[11px] text-slate-400">{formatTime(incident.timestamp)}</p>
                    </div>
                    <SeverityBadge severity={incident.severity} />
                  </button>
                ))}
              </div>
            )}
          </SectionCard>

          <SectionCard title="Operational Focus" subtitle="Use dedicated pages for incident response work.">
            <div className="grid gap-3 md:grid-cols-2">
              <button onClick={() => navigate("/fire/alerts")} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-4 text-left hover:bg-slate-50">
                <AlertTriangle size={18} className="text-amber-500" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-slate-900">New Alerts</p>
                  <p className="text-xs text-slate-500">Acknowledge incidents and post fire service status updates.</p>
                </div>
                <ChevronRight size={14} className="text-slate-400" />
              </button>
              <button onClick={() => navigate("/fire/active")} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-4 text-left hover:bg-slate-50">
                <Flame size={18} className="text-blue-500" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-slate-900">Active Incidents</p>
                  <p className="text-xs text-slate-500">Track responding cases, service logs, and vehicle telemetry.</p>
                </div>
                <ChevronRight size={14} className="text-slate-400" />
              </button>
            </div>
          </SectionCard>
        </div>

        <div className="space-y-5">
          <SectionCard title="Counts">
            <div className="space-y-3 text-sm text-slate-700">
              <div className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
                <span className="flex items-center gap-2"><AlertTriangle size={14} className="text-rose-500" /> Open alerts</span>
                <span className="font-bold">{openIncidentsCount}</span>
              </div>
              <div className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
                <span className="flex items-center gap-2"><Truck size={14} className="text-amber-500" /> Active deployments</span>
                <span className="font-bold">{respondingCount}</span>
              </div>
              <div className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
                <span className="flex items-center gap-2"><Activity size={14} className="text-blue-500" /> Live incident pushes</span>
                <span className="font-bold">{liveCount}</span>
              </div>
            </div>
          </SectionCard>
        </div>
      </div>
    </div>
  );
}
